import React, { useState } from "react";
import Link from "helpers/link";
import Stars from "components/stars";
import CrossSellItems from "components/cross-sell-items";
import QuantitySelector from "components/quantity-selector";
import { ReactComponent as CartIcon } from "assets/svgs/atc-icon.svg";
import { VariantSelectorBoxes } from "components/variant-selector";
import { CURRENCY } from "utils/constants";
import Money from "components/money";
import SrOnly from "components/sr-only";
import generateProductFinderUrl from "helpers/generate-product-finder-url";
import getProductAttributes from "helpers/get-product-attributes";

const hideOnProducts = ["Tarp", "Accessories"];

const ProductDetails = ({
    product,
    onVariantChange,
    quantity,
    handleQuantityChange,
    onAddToCart,
    loading,
    version,
    variantColors,
}) => {
    const [additionalItems, setAdditionalItems] = useState([]);

    const compareAtPrice = product?.currentVariant?.node?.compare_at_price
        ? product?.currentVariant?.node?.compare_at_price
        : product?.currentVariant?.node?.compareAtPrice;
    const savedAmount = ((compareAtPrice || 0) - (product?.currentVariant?.node?.price ? product.currentVariant.node.price : 0)).toFixed(2);

    const reviewStars = product?.metafields.edges.filter((metafield) => metafield.node.key === "review_stars");

    const checkoutCustomAttributes = product?.checkout_attributes && Object.entries(product.checkout_attributes)
        .reduce(
            (acc, attr) => (attr[1] !== null
                ? [...acc, { key: attr[0], value: attr[1].toString() }]
                : acc),
            [],
        );

    const handleItemsChange = (item) => setAdditionalItems([...additionalItems, ...[item]]);

    const fits = getProductAttributes(product);

    const showFits = fits &&
        !hideOnProducts.some((title) =>
            product.title.toLowerCase().includes(title.toLowerCase()) ||
            product.productType.toLowerCase().includes(title.toLowerCase())
        ) && !version;

    // Check if there is only one variant option with only 1 value
    let hasOneDropdownWithOneValue;
    if (product.productType === 'Accessories') {
        hasOneDropdownWithOneValue = !!((product?.options?.length === 1 && product.options[0].values.length === 1));
    } else {
        hasOneDropdownWithOneValue = !!((product?.options?.length === 1 && product.options[0].values.length === 0));
    }

    return (
        <>
            <div className="text-xwl md:text-2xl mb-1 text-blue-700">
                {product?.title}
                <SrOnly label={product?.title} />
            </div>
            <div className="flex flex-col md:flex-row">
                <div className="flex">
                    <Stars
                        rating={reviewStars[0]?.node?.value || 0}
                        className="!gap-0.5 mt-1"
                        starColor="fill-orange-701"
                    />
                    <Link
                        to="/"
                        className="relative before:content-['|'] before:absolute before:left-0 pl-2 ml-2 border-black font-semibold text-xs leading-6"
                    >
                        Write a review
                    </Link>
                </div>
                <div className="availability after:bg-[#53d45d] md:w-auto md:ml-auto md:text-base">In Stock</div>
            </div>
            {showFits && (
                <p className="mt-6 text-xs uppercase md:text-sm">
                    {product?.line_item?.id ? `For ${product?.line_item?.customAttributes.FITS}`
                        : (
                            <>
                                {`FITS ${fits}`}
                                <Link
                                    title="Go Back"
                                    className="font-medium underline ml-1"
                                    to={generateProductFinderUrl(product)}
                                >
                                    (Change)
                                </Link>
                            </>
                        )
                    }
                </p>
            )}
            {version ? (
                <p>
                    {" "}
                    {version}
                    {" "}
                </p>
            ) : ""}
            <div className="bg-[#fcfcfb] border border-[#e3e3e3] p-2.5 mt-4 rounded-t md:px-4 md:pt-5 md:pb-2.5">
                <div className="flex flex-col md:flex-row md:justify-between md:text-right">
                    <div className="p-0">
                        <p className="text-3xl inline-block font-bold text-[#fa621d] mb-1.5">
                            <Money value={{ currencyCode: CURRENCY, amount: product?.currentVariant?.node?.price }} />
                        </p>
                        <span className="sale-tag">On Sale</span>
                    </div>
                    {compareAtPrice && (
                        <div className="py-3.5 md:p-0">
                            <p className="text-sm py-0.5">
                                Regular Price:
                                {" "}
                                <span className="font-semibold line-through">
                                    <Money value={{ currencyCode: CURRENCY, amount: compareAtPrice }} />
                                </span>
                            </p>
                            <p className="text-sm py-0.5">
                                Save Today:
                                {" "}
                                <span className="font-semibold">
                                    <Money value={{ currencyCode: CURRENCY, amount: savedAmount }} />
                                </span>
                            </p>
                        </div>
                    )}
                </div>

                {product.handle !== 'seal-skin-covers-warranty-main' && (<>
                    {(product?.options?.length > 0 && !hasOneDropdownWithOneValue) && (
                        <div className="flex flex-col gap-2">
                            {product.options.map((option, optionIndex) => {
                                const selectedValue = product?.currentVariant?.node?.selectedOptions?.find((selectedOption) => selectedOption.name === option.name)?.value;
                                // Only show values that exist on a variant matching the selections of the options above
                                const parentOptions = product.options.slice(0, optionIndex);
                                const availableValues = option.values.filter((value) => product?.variants?.edges?.some(({ node }) => {
                                    const getValue = (name) => node.selectedOptions.find((selectedOption) => selectedOption.name === name)?.value;
                                    const getSelectedValue = (name) => product?.currentVariant?.node?.selectedOptions?.find((selectedOption) => selectedOption.name === name)?.value;
                                    return getValue(option.name) === value && parentOptions.every((parent) => getValue(parent.name) === getSelectedValue(parent.name));
                                }));

                                if (option?.name === "Color" || option?.name === "COLOR") {
                                    return (
                                        <VariantSelectorBoxes
                                            colors={availableValues}
                                            selected={selectedValue}
                                            onVariantChange={(value) => onVariantChange({ name: option.name, value })}
                                            variantColors={variantColors}
                                            key={option.name}
                                        />
                                    );
                                }
                                return (
                                    <div className="flex flex-col">
                                        <p className="md:text-sm mb-1">
                                            <strong className="font-semibold">
                                                {option.name}
                                                {" "}
                                                :
                                                {" "}
                                            </strong>
                                            {selectedValue}
                                        </p>
                                        <select
                                            className="border border-gray-601 py-1.5 px-4 pr-10 text-xs text-black w-full"
                                            value={selectedValue}
                                            onChange={(event) => onVariantChange({
                                                name: option.name,
                                                value: event.target.value
                                            })}
                                            key={option.name}
                                        >
                                            {availableValues.map((variantOption) => (
                                                <option
                                                    value={variantOption}
                                                    key={variantOption}
                                                >
                                                    {variantOption}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>)}
                <p
                    className="affirm-as-low-as text-xs md:text-sm mt-4"
                    data-page-type="product"
                    data-amount={product?.currentVariant?.node?.price && product.currentVariant.node.price * 100}
                />

                {product?.accessories && (
                    <CrossSellItems
                        data={product?.accessories}
                        setItems={handleItemsChange}
                    />
                )}

            </div>
            <div
                className="flex flex-col rounded-b bg-[#eff1ee] border border-[#e3e3e3] p-2.5 md:flex-row md:items-center md:p-4">
                <QuantitySelector
                    quantity={quantity}
                    onQuantityChange={(value) => handleQuantityChange(value)}
                    className="w-full bg-white my-0.5 !max-w-full md:!max-w-[9.5rem] md:m-0 md:mr-3"
                    isPDP
                />
                <button
                    type="button"
                    onClick={() => onAddToCart(quantity, additionalItems, checkoutCustomAttributes)}
                    className="btn-primary capitalize !max-w-full mb-1 mt-3 py-2.5 md:m-0"
                >
                    {loading ? "Adding To Cart" : "Add to Cart"}
                    <CartIcon fill="currentColor" className="ml-1" />
                </button>
            </div>
        </>
    );
};

export default ProductDetails;
